/* FERAL job page — sections/feral-job.liquid.
 *
 * The application form posts across origins to careers.feralclo.com (the
 * estate service; Shopify cannot take a file). A multipart POST with no custom
 * header is a "simple" request, so there is no preflight: do NOT set
 * Content-Type by hand, which would also strip the boundary the browser writes.
 *
 * Rules this file keeps:
 *  · Never destroy what someone typed. A failed send leaves every field as it
 *    was, and the text fields are kept in sessionStorage for this tab, so a
 *    reload or a wander off to find a CV loses nothing. Files cannot be kept.
 *  · Say what is wrong beside the thing that is wrong, and only once the person
 *    has had a go at it: no red on a field they have not reached yet.
 *  · The client checks are a courtesy; the service checks everything again and
 *    its sentence wins when the two disagree.
 *  · An application must not be lost to a bad connection. A send that fails on
 *    the network is tried again by itself (three times, then again the moment
 *    the phone is back online), and every try carries the same random key, so
 *    the service stores the application once however many tries reach it.
 *  · The form in the page is a real form (action, method, required fields). If
 *    this file never loads it still posts; when it does load it takes over.
 */
(function () {
  'use strict';

  var MB = 1024 * 1024;
  var MAX_FILE = 8 * MB;
  var EXTS = ['pdf', 'doc', 'docx', 'odt', 'pages', 'rtf', 'txt', 'jpg', 'jpeg', 'png', 'heic'];
  var EMAIL = /^[^\s@<>(),;:"[\]\\]+@[^\s@<>(),;:"[\]\\]+\.[^\s@<>(),;:"[\]\\]{2,}$/;
  var KEEP = ['name', 'email', 'phone', 'location', 'rtw', 'start', 'link', 'cover_text', 'cover_mode'];

  function init(root) {
    if (root.__fjb) return;
    root.__fjb = true;

    var endpoint = root.getAttribute('data-endpoint');
    var job = root.getAttribute('data-job') || 'general';
    var jobTitle = root.getAttribute('data-job-title') || '';
    var form = root.querySelector('[data-fjb-form]');
    var done = root.querySelector('[data-fjb-done]');
    var alertBox = root.querySelector('[data-fjb-alert]');
    var submit = root.querySelector('[data-fjb-submit]');
    var fill = root.querySelector('[data-fjb-fill]');
    var label = root.querySelector('[data-fjb-label]');
    var bar = root.querySelector('[data-fjb-bar]');
    var renderedAt = Date.now();
    var draftKey = 'feral-apply:' + job;
    var touched = {};
    var sending = false;
    var waitingForNetwork = false;
    // JUDGED: waits before each automatic retry. Long enough for a phone to find
    // signal again, short enough that the person is still looking at the button.
    var RETRY_AFTER = [2000, 5000, 12000];

    /* ── THE HEADER'S EDGE ───────────────────────────────────────────────
       Two sticky layers that collapse on scroll (105px at rest, 73px in
       motion) via a CSS transition that finishes after the last scroll event,
       so measure now and again at ~200ms and ~500ms.                        */
    (function chrome() {
      var write = function () {
        var edge = 0;
        var group = document.querySelectorAll('.shopify-section-group-header-group');
        for (var i = 0; i < group.length; i++) {
          var cs = getComputedStyle(group[i]);
          if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
          var r = group[i].getBoundingClientRect();
          if (r.height > 0 && r.height < 240 && r.bottom > edge) edge = r.bottom;
        }
        root.style.setProperty('--fjb-chrome', Math.max(0, Math.round(edge)) + 'px');
      };
      var t1, t2;
      var soon = function () {
        write();
        clearTimeout(t1); clearTimeout(t2);
        t1 = setTimeout(write, 200);
        t2 = setTimeout(write, 500);
      };
      write();
      addEventListener('scroll', soon, { passive: true });
      addEventListener('resize', soon, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(write);
    })();

    /* ── MOBILE BAR ───────────────────────────────────────────────────── */
    (function mobileBar() {
      var cta = root.querySelector('[data-fjb-cover-cta]');
      var apply = root.querySelector('#apply');
      if (!bar || !cta || !apply || !('IntersectionObserver' in window)) return;
      var ctaSeen = true, applySeen = false;
      var link = bar.querySelector('a');
      var paint = function () {
        var on = !ctaSeen && !applySeen && done.hidden;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (link) link.tabIndex = on ? 0 : -1;
      };
      new IntersectionObserver(function (es) { ctaSeen = es[0].isIntersecting; paint(); }).observe(cta);
      new IntersectionObserver(function (es) { applySeen = es[0].isIntersecting; paint(); }, { rootMargin: '0px 0px -30% 0px' }).observe(apply);
      root.__paintBar = paint;
    })();

    /* ── SHARE ────────────────────────────────────────────────────────── */
    (function share() {
      var btn = root.querySelector('[data-fjb-share]');
      if (!btn) return;
      var txt = btn.querySelector('[data-fjb-share-label]');
      var url = location.origin + location.pathname;
      btn.addEventListener('click', function () {
        if (navigator.share) {
          navigator.share({ title: jobTitle + ' at FERAL', url: url }).catch(function () {});
          return;
        }
        var ok = function () {
          txt.textContent = 'Link copied';
          setTimeout(function () { txt.textContent = 'Share this role'; }, 2200);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(ok, function () {});
      });
    })();

    if (!form || !endpoint) return;
    // From here the script does the checking, beside each field and in words.
    form.noValidate = true;

    // One random key per application, kept with the draft: a retry (ours, or the
    // person pressing send again after a reload) is recognised by the service and
    // answered with the reference the first try was given.
    function sendKey() {
      var k = null;
      try { k = sessionStorage.getItem(draftKey + ':key'); } catch (e) { k = null; }
      if (k && /^[a-f0-9]{32}$/.test(k)) return k;
      var bytes = new Uint8Array(16);
      if (window.crypto && crypto.getRandomValues) crypto.getRandomValues(bytes);
      else for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
      k = '';
      for (var j = 0; j < 16; j++) k += (bytes[j] < 16 ? '0' : '') + bytes[j].toString(16);
      try { sessionStorage.setItem(draftKey + ':key', k); } catch (e) { /* still unique for this page */ }
      return k;
    }
    var key = sendKey();

    var el = function (n) { return form.elements[n]; };
    var val = function (n) {
      var f = el(n);
      if (!f) return '';
      if (f instanceof RadioNodeList || (f.length && !f.tagName)) {
        for (var i = 0; i < f.length; i++) if (f[i].checked) return f[i].value;
        return '';
      }
      if (f.type === 'checkbox') return f.checked ? f.value : '';
      return f.value;
    };
    var mode = function () { return val('cover_mode') || 'file'; };

    /* ── ERRORS ───────────────────────────────────────────────────────── */
    function errEl(name) { return form.querySelector('[data-err="' + name + '"]'); }
    function fieldOf(name) {
      if (name === 'cover') return form.querySelector('[data-fjb-mode="' + mode() + '"]');
      if (name === 'consent') return form.querySelector('.fjb-check');
      var e = errEl(name);
      return e ? e.closest('.fjb-field') : null;
    }
    function setErr(name, msg) {
      var e = errEl(name), f = fieldOf(name);
      if (!e) return;
      e.textContent = msg || '';
      e.classList.toggle('is-on', !!msg);
      if (f) f.classList.toggle('is-bad', !!msg);
      var inputs = f ? f.querySelectorAll('input, textarea') : [];
      for (var i = 0; i < inputs.length; i++) {
        if (msg) inputs[i].setAttribute('aria-invalid', 'true'); else inputs[i].removeAttribute('aria-invalid');
      }
      if (summaryOn) summarise();
    }
    // The count above the button follows the fields: it drops as each one is
    // put right and goes when the last one is, so it never says "9 things"
    // over a finished form.
    var summaryOn = false;
    function summarise() {
      var n = form.querySelectorAll('.fjb-err.is-on').length;
      summaryOn = n > 0;
      alertBox.textContent = !n ? '' : n === 1 ? 'One thing needs a look before we can send this.' : n + ' things need a look before we can send this.';
      alertBox.hidden = !n;
    }

    function extOf(file) {
      var m = /\.([A-Za-z0-9]{1,8})$/.exec(file.name || '');
      return m ? m[1].toLowerCase() : '';
    }
    function fileProblem(file, what) {
      if (!file) return null;
      if (!file.size) return 'That file is empty. Please choose another.';
      if (EXTS.indexOf(extOf(file)) < 0) return 'We can’t open that type of file. Please use a PDF or Word document.';
      if (file.size > MAX_FILE) {
        return 'That file is ' + (file.size / MB).toFixed(1) + ' MB. Please keep ' + what + ' under 8 MB — saving it as a PDF usually shrinks it.';
      }
      return null;
    }

    var checks = {
      name: function () { return val('name').trim().length >= 2 ? null : 'Please tell us your full name.'; },
      email: function () { return EMAIL.test(val('email').trim()) ? null : 'Please enter a valid email address, like name@example.com.'; },
      phone: function () {
        // Any way of writing a number is fine ("+44 (0)7700 900123", "07700-900123 ext 2").
        var digits = (val('phone').match(/[0-9]/g) || []).length;
        return digits >= 7 ? null : 'Please enter a phone number we can call you on.';
      },
      location: function () { return val('location').trim() ? null : 'Please tell us where you’re based.'; },
      rtw: function () { return val('rtw') ? null : 'Please choose yes or no.'; },
      start: function () { return val('start') ? null : 'Please choose when you could start.'; },
      link: function () { return val('link').length <= 300 ? null : 'That link is too long.'; },
      cv: function () {
        var f = el('cv').files[0];
        return f ? fileProblem(f, 'your CV') : 'Please add your CV.';
      },
      cover: function () {
        if (mode() === 'text') {
          var n = val('cover_text').trim().length;
          if (n < 40) return n ? 'A few more lines, please: a couple of sentences at least.' : 'Please write your cover letter, or switch to “Upload a file”.';
          return null;
        }
        var f = el('cover').files[0];
        return f ? fileProblem(f, 'your cover letter') : 'Please add your cover letter, or switch to “Write it here”.';
      },
      consent: function () { return val('consent') ? null : 'Please tick this box so we can review your application.'; },
    };
    var ORDER = ['name', 'email', 'phone', 'location', 'rtw', 'start', 'link', 'cv', 'cover', 'consent'];

    function check(name) { var m = checks[name](); setErr(name, m); return m; }

    // A mistyped address is the one mistake that makes a good application
    // unanswerable. Offered, never applied for them, and only for the slips
    // people actually make on a phone keyboard.
    var TYPOS = {
      'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gmail.co': 'gmail.com',
      'gmail.con': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmaill.com': 'gmail.com', 'googlemail.con': 'googlemail.com',
      'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmail.con': 'hotmail.com', 'hotmail.co': 'hotmail.co.uk',
      'hotmail.co.k': 'hotmail.co.uk', 'hotmail.couk': 'hotmail.co.uk', 'outlok.com': 'outlook.com', 'outlook.con': 'outlook.com',
      'icloud.con': 'icloud.com', 'icloud.co': 'icloud.com', 'iclould.com': 'icloud.com', 'icoud.com': 'icloud.com',
      'yahoo.con': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yahoo.co.k': 'yahoo.co.uk', 'yahoo.couk': 'yahoo.co.uk',
      'live.co.k': 'live.co.uk', 'live.couk': 'live.co.uk', 'btinternet.con': 'btinternet.com'
    };
    var hint = form.querySelector('[data-fjb-email-hint]');
    var fix = form.querySelector('[data-fjb-email-fix]');
    function emailHint() {
      if (!hint || !fix) return;
      var v = val('email').trim(), at = v.lastIndexOf('@');
      var better = at > 0 ? TYPOS[v.slice(at + 1).toLowerCase()] : null;
      hint.hidden = !better;
      if (better) fix.textContent = v.slice(0, at + 1) + better;
    }
    if (fix) fix.addEventListener('click', function () {
      el('email').value = fix.textContent;
      hint.hidden = true;
      touched.email = true; check('email'); save();
      el('email').focus();
    });

    // Judged on leaving a field, never while it is being typed into; once a
    // field has shown an error it re-checks as it is typed, so it clears the
    // moment it is right.
    form.addEventListener('focusout', function (e) {
      var n = e.target.name;
      if (n === 'email') emailHint();
      if (!checks[n] || !e.target.value) return;
      touched[n] = true;
      check(n);
    });
    form.addEventListener('input', function (e) {
      var n = e.target.name === 'cover_text' ? 'cover' : e.target.name;
      if (touched[n] && checks[n]) check(n);
      if (e.target.name === 'cover_text') count();
      if (e.target.name === 'email' && hint && !hint.hidden) emailHint();
      save();
    });
    form.addEventListener('change', function (e) {
      var n = e.target.name;
      if (n === 'rtw' || n === 'start' || n === 'consent') { touched[n] = true; check(n); }
      save();
    });

    /* ── FILES ────────────────────────────────────────────────────────── */
    function wireFile(field) {
      var name = field.getAttribute('data-fjb-file');
      var input = field.querySelector('input[type=file]');
      var drop = field.querySelector('[data-fjb-drop]');
      var card = field.querySelector('[data-fjb-card]');
      var paint = function () {
        var f = input.files[0];
        drop.hidden = !!f;
        card.hidden = !f;
        if (f) {
          field.querySelector('[data-fjb-fname]').textContent = f.name;
          field.querySelector('[data-fjb-fsize]').textContent = f.size < MB ? Math.max(1, Math.round(f.size / 1024)) + ' KB' : (f.size / MB).toFixed(1) + ' MB';
          field.querySelector('[data-fjb-ext]').textContent = (extOf(f) || 'FILE').toUpperCase().slice(0, 4);
        }
        touched[name] = true;
        check(name);
      };
      input.addEventListener('change', paint);
      field.querySelector('[data-fjb-change]').addEventListener('click', function () { input.click(); });
      field.querySelector('[data-fjb-remove]').addEventListener('click', function () {
        input.value = '';
        drop.hidden = false; card.hidden = true;
        setErr(name, null);
        input.focus();
      });
      // Drag and drop where there is a pointer that drags. A dropped file is
      // put into the real input, so there is one source of truth for the send.
      var depth = 0;
      drop.addEventListener('dragenter', function (e) { e.preventDefault(); depth++; drop.classList.add('is-over'); });
      drop.addEventListener('dragover', function (e) { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; });
      drop.addEventListener('dragleave', function () { if (--depth <= 0) { depth = 0; drop.classList.remove('is-over'); } });
      drop.addEventListener('drop', function (e) {
        e.preventDefault(); depth = 0; drop.classList.remove('is-over');
        var files = e.dataTransfer && e.dataTransfer.files;
        if (!files || !files.length) return;
        try {
          var dt = new DataTransfer();
          dt.items.add(files[0]);
          input.files = dt.files;
        } catch (err) { return; }
        paint();
      });
    }
    var fileFields = form.querySelectorAll('[data-fjb-file]');
    for (var i = 0; i < fileFields.length; i++) wireFile(fileFields[i]);
    // A file dropped anywhere else on the page must not navigate away from it.
    addEventListener('dragover', function (e) { if (e.target.closest && !e.target.closest('[data-fjb-drop]')) e.preventDefault(); });
    addEventListener('drop', function (e) { if (e.target.closest && !e.target.closest('[data-fjb-drop]')) e.preventDefault(); });

    /* ── COVER LETTER: FILE OR WRITTEN ────────────────────────────────── */
    var countEl = form.querySelector('[data-fjb-count]');
    function count() {
      if (countEl) countEl.textContent = val('cover_text').length.toLocaleString('en-GB') + ' / 6,000';
    }
    function showMode() {
      var m = mode();
      var parts = form.querySelectorAll('[data-fjb-mode]');
      for (var i = 0; i < parts.length; i++) parts[i].hidden = parts[i].getAttribute('data-fjb-mode') !== m;
      setErr('cover', null);
      form.querySelector('[data-fjb-mode="file"]').classList.remove('is-bad');
      form.querySelector('[data-fjb-mode="text"]').classList.remove('is-bad');
    }
    var modes = form.querySelectorAll('input[name=cover_mode]');
    for (var j = 0; j < modes.length; j++) {
      modes[j].addEventListener('change', function () {
        showMode();
        if (mode() === 'text') el('cover_text').focus();
      });
    }

    /* ── DRAFT ────────────────────────────────────────────────────────── */
    var saveT;
    function save() {
      clearTimeout(saveT);
      saveT = setTimeout(function () {
        try {
          var d = {};
          KEEP.forEach(function (k) { d[k] = val(k); });
          sessionStorage.setItem(draftKey, JSON.stringify(d));
        } catch (e) { /* private mode, or storage full: the form still works */ }
      }, 250);
    }
    (function restore() {
      var d;
      try { d = JSON.parse(sessionStorage.getItem(draftKey) || 'null'); } catch (e) { d = null; }
      if (!d) return;
      var any = false;
      KEEP.forEach(function (k) {
        var v = d[k];
        if (!v) return;
        var f = el(k);
        if (!f) return;
        if (f.length && !f.tagName) {
          for (var i = 0; i < f.length; i++) f[i].checked = f[i].value === v;
        } else {
          f.value = v;
        }
        if (k !== 'cover_mode') any = true;
      });
      showMode();
      count();
      if (any) form.querySelector('[data-fjb-restored]').hidden = false;
    })();

    /* ── SEND ─────────────────────────────────────────────────────────── */
    function say(msg) {
      summaryOn = false;
      alertBox.textContent = msg;
      alertBox.hidden = !msg;
    }
    function busy(on, pct) {
      sending = on;
      submit.setAttribute('aria-busy', on ? 'true' : 'false');
      submit.disabled = on;
      fill.style.width = on ? (pct || 0) + '%' : '0';
      if (!on) label.textContent = 'Send application';
      else if (pct >= 100) label.textContent = 'Almost there…';
      else label.textContent = pct ? 'Sending… ' + pct + '%' : 'Sending…';
    }
    function jump(target) {
      if (!target) return;
      var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
    function focusField(name) {
      var f = fieldOf(name);
      if (!f) return;
      jump(f);
      var target = f.querySelector('input:not([type=hidden]):not([hidden]), textarea');
      if (name === 'cv' || name === 'cover') target = f.querySelector('input[type=file]') || f.querySelector('textarea');
      if (target) setTimeout(function () { target.focus({ preventScroll: true }); }, 350);
    }

    function build() {
      var fd = new FormData();
      fd.append('job', job);
      fd.append('job_title', jobTitle);
      fd.append('name', val('name').trim());
      fd.append('email', val('email').trim());
      fd.append('phone', val('phone').trim());
      fd.append('location', val('location').trim());
      fd.append('rtw', val('rtw'));
      fd.append('start', val('start'));
      fd.append('link', val('link').trim());
      fd.append('consent', 'yes');
      fd.append('website', val('website'));
      // How long the page had been open, counted here: the service never
      // compares its clock with the phone's.
      fd.append('el', String(Date.now() - renderedAt));
      fd.append('key', key);
      if (mode() === 'text') fd.append('cover_text', val('cover_text'));
      fd.append('cv', el('cv').files[0]);
      if (mode() === 'file' && el('cover').files[0]) fd.append('cover', el('cover').files[0]);
      return fd;
    }

    // attempt 0 is the person pressing the button; 1..3 are ours.
    function post(attempt) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', endpoint, true);
      xhr.timeout = 5 * 60 * 1000;
      xhr.upload.onprogress = function (ev) {
        if (ev.lengthComputable) busy(true, Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
      };
      // The network failed, or something between here and FERAL did (a gateway
      // answering 502/504, or anything that is not the service's own JSON).
      var dropped = function () {
        if (attempt < RETRY_AFTER.length) {
          busy(true, 0);
          label.textContent = 'Connection dropped. Trying again…';
          setTimeout(function () { post(attempt + 1); }, RETRY_AFTER[attempt]);
          return;
        }
        busy(false);
        waitingForNetwork = true;
        say(navigator.onLine === false
          ? 'You’re offline. Keep this page open: we’ll send your application the moment you’re back online. Nothing you’ve entered has been lost.'
          : 'We couldn’t reach FERAL. Please check your connection and press send again. Nothing you’ve entered has been lost.');
      };
      xhr.onload = function () {
        var body = null;
        try { body = JSON.parse(xhr.responseText); } catch (err) { body = null; }
        if (xhr.status === 201 && body && body.ok) return success(body.ref);
        if (!body && (xhr.status === 0 || xhr.status >= 500)) return dropped();
        busy(false);
        var msg = (body && body.error) || 'Something went wrong on our side. Please try again in a minute.';
        if (body && body.field && errEl(body.field)) {
          setErr(body.field, msg);
          summarise();
          focusField(body.field);
        } else {
          say(msg + ' Nothing you’ve entered has been lost.');
        }
      };
      xhr.onerror = dropped;
      xhr.ontimeout = dropped;
      busy(true, 0);
      try { xhr.send(build()); } catch (err) { dropped(); }
    }

    function trySend() {
      if (sending) return;
      waitingForNetwork = false;
      say('');
      var bad = [];
      ORDER.forEach(function (n) { touched[n] = true; if (check(n)) bad.push(n); });
      if (bad.length) {
        summarise();
        focusField(bad[0]);
        return;
      }
      post(0);
    }
    form.addEventListener('submit', function (e) { e.preventDefault(); trySend(); });
    // Back online after every retry failed: send it without being asked.
    addEventListener('online', function () { if (waitingForNetwork && !sending && !form.hidden) trySend(); });
    // Closing the tab mid-upload loses the application; the browser asks first.
    addEventListener('beforeunload', function (e) { if (sending) { e.preventDefault(); e.returnValue = ''; } });

    function success(ref) {
      try { sessionStorage.removeItem(draftKey); sessionStorage.removeItem(draftKey + ':key'); } catch (e) { /* nothing to clear */ }
      var first = val('name').trim().split(/\s+/)[0];
      done.querySelector('[data-fjb-first]').textContent = first ? ', ' + first : '';
      done.querySelector('[data-fjb-ref]').textContent = ref || '';
      form.hidden = true;
      done.hidden = false;
      busy(false);
      if (root.__paintBar) root.__paintBar();
      jump(done.parentNode);
      setTimeout(function () { done.focus({ preventScroll: true }); }, 400);
    }

    showMode();
    count();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-fjb]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', boot);
})();
